/**
 * Otto Suenson wishlist — localStorage-based guest + logged-in support.
 */
(function () {
  const STORAGE_KEY = 'ottosuenson:wishlist';
  const UPDATE_EVENT = 'wishlist:updated';

  const config = window.OttoWishlist || {};
  const i18n = config.i18n || {};

  function readItems() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function writeItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    dispatchUpdate(items);
  }

  function dispatchUpdate(items) {
    const detail = { items, count: items.length };
    document.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail }));
  }

  function isSaved(handle) {
    return readItems().some((item) => item.handle === handle);
  }

  function getProductFromButton(button) {
    return {
      id: Number(button.dataset.productId),
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      variantId: Number(button.dataset.variantId),
    };
  }

  function toggleProduct(product) {
    const items = readItems();
    const index = items.findIndex((item) => item.handle === product.handle);

    if (index >= 0) {
      items.splice(index, 1);
      writeItems(items);
      return { added: false, items };
    }

    items.unshift({
      id: product.id,
      handle: product.handle,
      title: product.title,
      url: product.url,
      image: product.image,
      variantId: product.variantId,
      addedAt: Date.now(),
    });
    writeItems(items);
    return { added: true, items };
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, config.moneyFormat);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'da-DK', {
      style: 'currency',
      currency: config.currency || 'DKK',
    }).format(cents / 100);
  }

  function showToast(message) {
    let container = document.querySelector('.toast-container');

    if (!container) {
      container = document.createElement('div');
      container.className =
        'toast-container fixed right-4 bottom-4 z-50 flex flex-col items-end gap-2';
      document.body.appendChild(container);
    }

    const toast = document.createElement('div');
    toast.className =
      'theme-white bg-background text-foreground rounded-element border-brand-grey-100 border px-4 py-3 text-body-sm shadow-md';
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    container.appendChild(toast);

    window.setTimeout(() => {
      toast.remove();
      if (!container.children.length) container.remove();
    }, 2800);
  }

  function syncButton(button) {
    const handle = button.dataset.productHandle;
    const saved = isSaved(handle);

    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', saved ? i18n.remove || 'Remove' : i18n.add || 'Add');

    const outline = button.querySelector('.wishlist-button__icon--outline');
    const filled = button.querySelector('.wishlist-button__icon--filled');

    if (outline) outline.classList.toggle('hidden', saved);
    if (filled) filled.classList.toggle('hidden', !saved);
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-wishlist-button]').forEach(syncButton);
  }

  function syncCountBadge() {
    const count = readItems().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
      if (count > 0) {
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.classList.remove('hidden');
        badge.classList.add('flex');
      } else {
        badge.textContent = '';
        badge.classList.add('hidden');
        badge.classList.remove('flex');
      }
    });
  }

  function handleButtonClick(event) {
    const button = event.currentTarget;
    const product = getProductFromButton(button);
    const result = toggleProduct(product);

    syncButton(button);
    syncCountBadge();
    showToast(result.added ? i18n.added : i18n.removed);
    if (!result.added) {
      button.closest('[data-wishlist-grid] .os-product-card')?.remove();
      updateWishlistPageState();
    }
  }

  function bindButtons(root = document) {
    const buttons = [...root.querySelectorAll('[data-wishlist-button]')];
    if (root.matches?.('[data-wishlist-button]')) buttons.unshift(root);
    buttons.forEach((button) => {
      if (button.dataset.wishlistBound === 'true') return;
      button.dataset.wishlistBound = 'true';
      syncButton(button);
      button.addEventListener('click', handleButtonClick);
    });
  }

  async function fetchProduct(handle) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);

    if (!response.ok) return null;

    return response.json();
  }

  async function fetchEditorialCard(item) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(item.handle)}?section_id=editorial-wishlist-card`);
      if (!response.ok) return null;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = doc.querySelector('.os-product-card');
      if (!card) return null;
      card.dataset.productHandle = item.handle;
      return card;
    } catch {
      return null;
    }
  }

  function renderWishlistCard(product, savedItem) {
    const card = document.createElement('article');
    card.className = 'wishlist-card flex flex-col h-full';
    card.dataset.productHandle = product.handle;

    const imageSrc = product.featured_image || savedItem.image || '';
    const price = formatMoney(product.price);
    const compareAt =
      product.compare_at_price && product.compare_at_price > product.price
        ? formatMoney(product.compare_at_price)
        : '';

    card.innerHTML = `
      <a href="${product.url}" class="group/anchor block w-full">
        <div class="rounded-element relative overflow-hidden bg-brand-grey-100 flex w-full items-center justify-center aspect-portrait">
          ${
            imageSrc
              ? `<img src="${imageSrc}" alt="${product.title.replace(/"/g, '&quot;')}" class="product-image img-default shrink-0 w-full h-full object-cover md:group-hover/anchor:scale-105 transition ease-in-out" loading="lazy" width="600" height="750">`
              : ''
          }
        </div>
        <div class="text-foreground block py-4">
          <h3 class="text-heading-sm font-bold group-hover/anchor:underline">${product.title}</h3>
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <span class="text-body-sm font-medium">${price}</span>
            ${compareAt ? `<span class="text-body-sm text-brand-grey-400 line-through">${compareAt}</span>` : ''}
          </div>
        </div>
      </a>
      <div class="mt-auto pt-2">
        <button type="button" class="wishlist-card__remove text-body-xs underline underline-offset-4 hover:no-underline" data-wishlist-remove data-product-handle="${product.handle}">
          ${i18n.remove || 'Remove'}
        </button>
      </div>
    `;

    const removeButton = card.querySelector('[data-wishlist-remove]');
    removeButton.addEventListener('click', () => {
      toggleProduct({ handle: product.handle });
      card.remove();
      syncAllButtons();
      syncCountBadge();
      updateWishlistPageState();
      showToast(i18n.removed);
    });

    return card;
  }

  function updateWishlistPageState() {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page) return;

    const grid = page.querySelector('[data-wishlist-grid]');
    const empty = page.querySelector('[data-wishlist-empty]');
    const countLabel = page.querySelector('[data-wishlist-page-count]');
    const items = readItems();

    if (countLabel) {
      countLabel.textContent =
        items.length === 1
          ? (i18n.count_one || '{{ count }} product').replace('{{ count }}', '1')
          : (i18n.count_other || '{{ count }} products').replace('{{ count }}', String(items.length));
      countLabel.hidden = items.length === 0;
    }

    if (!grid || !empty) return;

    if (!items.length) {
      grid.replaceChildren();
      grid.hidden = true;
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    grid.hidden = false;
  }

  async function renderWishlistPage() {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page) return;

    const grid = page.querySelector('[data-wishlist-grid]');
    const loading = page.querySelector('[data-wishlist-loading]');
    const items = readItems();

    if (!items.length) {
      if (loading) loading.hidden = true;
      updateWishlistPageState();
      return;
    }

    if (loading) loading.hidden = false;
    grid?.replaceChildren();

    const results = await Promise.all(
      items.map(async (item) => {
        const editorialCard = await fetchEditorialCard(item);
        if (editorialCard) return editorialCard;
        const product = await fetchProduct(item.handle);
        return product ? renderWishlistCard(product, item) : null;
      })
    );

    results.filter(Boolean).forEach((card) => grid?.appendChild(card));
    if (grid) bindButtons(grid);

    if (loading) loading.hidden = true;
    updateWishlistPageState();
  }

  function init() {
    bindButtons();
    syncCountBadge();
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType === 1) bindButtons(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener(UPDATE_EVENT, () => {
      syncAllButtons();
      syncCountBadge();
    });

    document.addEventListener('shopify:section:load', (event) => {
      bindButtons(event.target);
      syncAllButtons();
      syncCountBadge();
    });

    renderWishlistPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.OttoWishlist = {
    ...config,
    readItems,
    isSaved,
    toggleProduct,
    syncAllButtons,
    syncCountBadge,
  };
})();
